import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertCircle,
  Copy,
  FileSignature,
  Loader2,
  Plus,
  Receipt,
  Search,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { toast } from '../lib/dialogs'
import { SELECTOR_PAGE_SIZE } from '../lib/constants'
import { formatCents } from '../lib/money'
import {
  QuoteStatusLabel,
  type ClientListResponse,
  type QuoteListResponse,
  type QuoteStatusT,
} from '@toga/shared'

const PAGE_SIZE = 50

export const QUOTE_STATUS_BADGE: Record<QuoteStatusT, string> = {
  BORRADOR: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
  ENVIADO: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40',
  EN_NEGOCIACION: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  ACEPTADO: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  RECHAZADO: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
  CADUCADO: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  FACTURADO: 'bg-toga-500/15 text-toga-700 dark:text-toga-300 border-toga-500/30',
}

export function QuoteStatusBadge({ status }: { status: QuoteStatusT }) {
  const { t } = useTranslation()
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${QUOTE_STATUS_BADGE[status]}`}>
      {t('enums:QuoteStatus.' + status)}
    </span>
  )
}

export default function Quotes() {
  const { t } = useTranslation('quotes')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<QuoteListResponse | null>(null)
  const [clients, setClients] = useState<Array<{ id: string; displayName: string }>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState(searchParams.get('q') ?? '')

  const status = searchParams.get('status') ?? ''
  const clientId = searchParams.get('clientId') ?? ''
  const year = searchParams.get('year') ?? ''
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1)

  useEffect(() => {
    api
      .get<ClientListResponse>(`/clients?pageSize=${SELECTOR_PAGE_SIZE}&sortBy=name&sortDir=asc`)
      .then((r) => setClients(r.items.map((c) => ({ id: c.id, displayName: c.displayName }))))
      .catch(() => setClients([]))
  }, [])

  // Debounce de la búsqueda hacia la URL.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      if (q.trim()) next.set('q', q.trim())
      else next.delete('q')
      next.delete('page')
      setSearchParams(next, { replace: true })
    }, 300)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      const qParam = searchParams.get('q')
      if (qParam) params.set('q', qParam)
      if (status) params.set('status', status)
      if (clientId) params.set('clientId', clientId)
      if (year) params.set('year', year)
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      setData(await api.get<QuoteListResponse>(`/quotes?${params.toString()}`))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.load'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [searchParams, status, clientId, year, page, t])

  useEffect(() => {
    void load()
  }, [load])

  // Acción id en curso (deshabilita doble click). null = ninguna.
  const [busyId, setBusyId] = useState<string | null>(null)

  const duplicateQuote = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      setBusyId(id)
      try {
        const { quote } = await api.post<{ quote: { id: string } }>(
          `/quotes/${id}/duplicate`,
          {},
        )
        navigate(`/quotes/${quote.id}`)
      } catch (err) {
        const body =
          err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
        toast.error(body?.error ?? (err instanceof Error ? err.message : t('errors.duplicate')))
      } finally {
        setBusyId(null)
      }
    },
    [navigate, t],
  )

  const toInvoice = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      setBusyId(id)
      try {
        const { invoiceId } = await api.post<{ invoiceId: string }>(
          `/quotes/${id}/to-invoice`,
          {},
        )
        navigate(`/billing/${invoiceId}`)
      } catch (err) {
        const body =
          err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
        toast.error(body?.error ?? (err instanceof Error ? err.message : t('errors.toInvoice')))
      } finally {
        setBusyId(null)
      }
    },
    [navigate, t],
  )

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page') next.delete('page')
    setSearchParams(next, { replace: true })
  }

  const years: number[] = []
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y)
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <FileSignature size={20} className="text-toga-700 dark:text-toga-300" />
            {t('title')}
          </h1>
          {data && (
            <p className="text-xs t-muted">
              {data.total} {data.total !== 1 ? t('countPlural') : t('countSingular')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/quotes/new')}
          className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-2"
        >
          <Plus size={15} /> {t('newButton')}
        </button>
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
        <Select
          label={t('filters.status')}
          value={status}
          onChange={(v) => setParam('status', v)}
          options={[
            { v: '', l: t('filters.all') },
            ...Object.keys(QuoteStatusLabel).map((k) => ({ v: k, l: t('enums:QuoteStatus.' + k) })),
          ]}
        />
        <Select
          label={t('filters.client')}
          value={clientId}
          onChange={(v) => setParam('clientId', v)}
          options={[{ v: '', l: t('filters.all') }, ...clients.map((c) => ({ v: c.id, l: c.displayName }))]}
        />
        <Select
          label={t('filters.year')}
          value={year}
          onChange={(v) => setParam('year', v)}
          options={[{ v: '', l: t('filters.all') }, ...years.map((y) => ({ v: String(y), l: String(y) }))]}
        />
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-3 py-2 text-left">{t('table.number')}</th>
              <th className="px-3 py-2 text-left">{t('table.client')}</th>
              <th className="px-3 py-2 text-left">{t('table.concept')}</th>
              <th className="px-3 py-2 text-left">{t('table.status')}</th>
              <th className="px-3 py-2 text-right">{t('table.total')}</th>
              <th className="px-3 py-2 text-left">{t('table.date')}</th>
              <th className="px-3 py-2 text-left">{t('table.validity')}</th>
              <th className="px-3 py-2 text-right w-32">{t('table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> {t('loading')}
                </td>
              </tr>
            )}
            {!loading && data && data.items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center">
                  <FileSignature size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {t('emptyHint')}
                  </p>
                </td>
              </tr>
            )}
            {data?.items.map((it) => (
              <tr
                key={it.id}
                onClick={() => navigate(`/quotes/${it.id}`)}
                className="border-t border-base/40 hover:bg-hover cursor-pointer"
              >
                <td className="px-3 py-2 font-mono text-toga-700 dark:text-toga-300">{it.number}</td>
                <td className="px-3 py-2 t-primary">{it.clientDisplayName}</td>
                <td className="px-3 py-2 t-secondary truncate max-w-[260px]">
                  {it.title ?? <span className="t-faint">—</span>}
                  {it.opportunityCode && (
                    <span className="ml-2 text-[10px] font-mono t-faint">{it.opportunityCode}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <QuoteStatusBadge status={it.status} />
                </td>
                <td className="px-3 py-2 text-right font-mono t-primary">
                  {formatCents(it.totalCents)}
                </td>
                <td className="px-3 py-2 text-xs t-muted">
                  {new Date(it.issueDate).toLocaleDateString(currentLocaleTag())}
                </td>
                <td className="px-3 py-2 text-xs t-muted">
                  {it.validUntil ? new Date(it.validUntil).toLocaleDateString(currentLocaleTag()) : '—'}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={(e) => void duplicateQuote(e, it.id)}
                    disabled={busyId === it.id}
                    title={t('actions.duplicateTitle')}
                    className="t-faint hover:text-toga-700 dark:hover:text-toga-200 px-1.5 py-1 rounded disabled:opacity-50"
                  >
                    {busyId === it.id ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Copy size={13} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => void toInvoice(e, it.id)}
                    disabled={busyId === it.id || it.status !== 'ACEPTADO' || Boolean(it.invoiceId)}
                    title={
                      it.invoiceId
                        ? t('actions.alreadyInvoiced')
                        : it.status !== 'ACEPTADO'
                          ? t('actions.onlyAccepted')
                          : t('actions.convertToInvoice')
                    }
                    className="t-faint hover:text-emerald-700 dark:hover:text-emerald-300 px-1.5 py-1 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Receipt size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards — móvil */}
      <div className="md:hidden space-y-2">
        {loading && (
          <div className="surface p-6 text-center t-muted text-sm">
            <Loader2 size={14} className="inline-block animate-spin mr-2" /> {t('loading')}
          </div>
        )}
        {!loading && data && data.items.length === 0 && (
          <div className="surface p-6 text-center">
            <FileSignature size={24} className="mx-auto t-faint mb-2" />
            <p className="t-muted text-sm">{t('emptyShort')}</p>
          </div>
        )}
        {!loading &&
          data?.items.map((it) => (
            <Link
              key={it.id}
              to={`/quotes/${it.id}`}
              className="block surface p-3 hover:bg-hover/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <span className="font-mono text-xs text-toga-700 dark:text-toga-300">{it.number}</span>
                <QuoteStatusBadge status={it.status} />
              </div>
              <div className="t-primary text-sm font-medium truncate">{it.clientDisplayName}</div>
              {it.title && <div className="t-secondary text-xs truncate mt-0.5">{it.title}</div>}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-base/40 text-[11px]">
                <span className="t-muted">
                  {new Date(it.issueDate).toLocaleDateString(currentLocaleTag())}
                  {it.validUntil && (
                    <span className="t-faint">
                      {' '}
                      · {t('expiresOn', { date: new Date(it.validUntil).toLocaleDateString(currentLocaleTag()) })}
                    </span>
                  )}
                </span>
                <span className="font-mono t-primary font-semibold">
                  {formatCents(it.totalCents)}
                </span>
              </div>
            </Link>
          ))}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs t-muted">
          <span>
            {t('pagination.pageOf', { page: data.page, total: totalPages })}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={data.page <= 1}
              onClick={() => setParam('page', String(data.page - 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              {t('pagination.previous')}
            </button>
            <button
              type="button"
              disabled={data.page >= totalPages}
              onClick={() => setParam('page', String(data.page + 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              {t('pagination.next')}
            </button>
          </div>
        </div>
      )}

      <p className="text-xs t-faint">
        {t('footerNote')}
      </p>
    </div>
  )
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { v: string; l: string }[]
}) {
  return (
    <label className="text-xs t-muted flex items-center gap-2">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  )
}
